import React, { useCallback, useMemo, useRef } from "react";
import JoditEditor from "jodit-react";
import cookie from "js-cookie";
import { handleImageLink } from "../../helpers/Function";

const JoditEditorComponent = ({ value, onChange, placeholder = "" }) => {
	const editor = useRef(null);
	const skipNextBlurSync = useRef(false);
	const editorValue = typeof value === "string" ? value : "";

	const handleMouseDownCapture = useCallback(event => {
		const target = event?.target;
		if (!target || typeof target.closest !== "function") return;

		const inUploadBox = target.closest(".jodit-drag-and-drop__file-box");
		if (inUploadBox) {
			skipNextBlurSync.current = true;
		}
	}, []);

	const handleBlur = useCallback(
		newContent => {
			if (typeof onChange !== "function") return;

			if (skipNextBlurSync.current) {
				skipNextBlurSync.current = false;
				return;
			}

			const content = typeof newContent === "string" ? newContent : "";
			if (content === editorValue) return;

			const currentScrollY = window.scrollY;
			onChange(content);
			requestAnimationFrame(() => {
				window.scrollTo({ top: currentScrollY });
			});
		},
		[editorValue, onChange]
	);

	const handleEditorChange = useCallback(
		newContent => {
			if (typeof onChange !== "function") return;

			const content = typeof newContent === "string" ? newContent : "";
			if (content === editorValue) return;

			onChange(content);
		},
		[editorValue, onChange]
	);

	const config = useMemo(
		() => ({
			readonly: false,
			placeholder,
			height: 500,
			toolbarButtonSize: "middle",
			buttons: [
				"bold",
				"italic",
				"underline",
				"strikethrough",
				"|",
				"ul",
				"ol",
				"|",
				"outdent",
				"indent",
				"|",
				// "font",
				"fontsize",
				"brush",
				"paragraph",
				"|",
				"image",
				"video",
				"table",
				"link",
				"|",
				"align",
				"undo",
				"redo",
				"|",
				"hr",
				"eraser",
				"|",
				"fullsize",
				"print",
				"preview",
			],
			uploader: {
				url: `${process.env.REACT_APP_API_URL}/upload`,
				method: "POST",
				format: "json",
				insertImageAsBase64URI: false,
				imagesExtensions: ["jpg", "jpeg", "png", "gif", "webp", "svg"],
				withCredentials: false,
				headers: {
					Authorization: `Bearer ${cookie.get("token")}`,
					Accept: "application/json",
				},
				filesVariableName: () => "file",
				process: resp => {
					const uploadedName = resp?.data?.name;
					const imageUrl = handleImageLink(uploadedName || "");
					const imageExtensionRegex = /\.(jpg|jpeg|png|gif|webp|svg)(\?.*)?$/i;
					const isImage = imageExtensionRegex.test(imageUrl);

					return {
						files: imageUrl ? [imageUrl] : [],
						isImages: imageUrl ? [isImage] : [],
						path: "",
						baseurl: "",
						error: imageUrl ? 0 : 1,
						msg: imageUrl ? "Uploaded" : "Upload failed",
					};
				},
				isSuccess: resp => Boolean(resp?.data?.name),
				getMessage: resp =>
					resp?.message || (resp?.data?.name ? "Uploaded" : "Upload failed"),
			},
			filebrowser: {
				ajax: {
					url: "",
				},
			},
			imageDefaultWidth: 300,
		}),
		[placeholder]
	);

	return (
		<div onMouseDownCapture={handleMouseDownCapture}>
			<JoditEditor
				ref={editor}
				value={editorValue}
				config={config}
				onBlur={handleBlur}
				onChange={handleEditorChange}
			/>
		</div>
	);
};

export default JoditEditorComponent;
